import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RiEditLine } from "react-icons/ri";
import fakeProfile from "../../assets/images/avatar.webp";
import useCompanyStore from "./../../store/useCompanyStore";
import useDepartmentStore from "../../store/useDepartmentStore";
import useDepartmentWiseEmployeeStore from "../../store/useDepartmentWiseEmployeeStore";
import useEmployeeIdStore from "../../store/useEmployeeIdStore";
import { BiSearch } from "react-icons/bi";

const PermissionManagement = () => {
  const [selectedCompany, setSelectedCompany] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const { companyList, fetchCompanies } = useCompanyStore();
  const { departmentList, getDepartmentList } = useDepartmentStore();
  const { departmentWiseEmployees, getDepartmentWiseEmployees } =
    useDepartmentWiseEmployeeStore();
  const { setEachEmployeeInfo } = useEmployeeIdStore();

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    getDepartmentList(selectedCompany);
  }, [selectedCompany]);

  const handleDepartmentWiseEmployees = (id) => {
    getDepartmentWiseEmployees(id);
    setCurrentPage(1);
  };

  const filteredEmployees =
    departmentWiseEmployees?.filter((employee) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      return (
        employee?.employee_id?.toLowerCase().includes(query) ||
        employee?.employee_full_name?.toLowerCase().includes(query) ||
        employee?.roles?.[0]?.role_name?.toLowerCase().includes(query)
      );
    }) || [];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const totalItems = filteredEmployees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handleEditPermissions = (data) => {
    setEachEmployeeInfo(data);
    navigate("/edit-employee-permissions");
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // console.log(departmentWiseEmployees);

  return (
    <div className="w-full p-7">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-medium-dark-gray">
            Permission Management
          </h1>
        </div>
        <div className="emp__search-box relative">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-8"
          />
          <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
            {searchQuery ? (
              <button
                onClick={clearSearch}
                className="text-gray-400 hover:text-gray-600 text-sm"
                title="Clear search"
              >
                ✕
              </button>
            ) : (
              <BiSearch className="font-icon" />
            )}
          </span>
        </div>
      </div>

      <div className="flex gap-4 mt-10">
        <select
          name="company"
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-pms"
        >
          <option value="">Select Company</option>
          {companyList?.map((company, index) => (
            <option value={company.company_name} key={index}>
              {company.company_name}
            </option>
          ))}
        </select>

        <select
          name="department"
          value={departmentId}
          onChange={(e) => {
            setDepartmentId(e.target.value);
            handleDepartmentWiseEmployees(e.target.value);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:primary-pms"
        >
          <option value="">Select Department</option>
          {departmentList?.map((department, index) => (
            <option value={department.id} key={index}>
              {department.department_name}
            </option>
          ))}
        </select>
      </div>

      {/* Search Results Info */}
      {searchQuery && (
        <div className="mt-4 text-sm text-gray-600">
          {totalItems > 0 ? (
            <span>
              Found {totalItems} employee(s) matching "{searchQuery}"
            </span>
          ) : (
            <span>No employees found matching "{searchQuery}"</span>
          )}
        </div>
      )}

      <div className="mt-10">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left">
                SL No.
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Employee Id
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Employee Name
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Position
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {currentEmployees && currentEmployees.length > 0 ? (
              currentEmployees.map((data, index) => (
                <tr key={data.employee_id || index}>
                  <td className="border border-gray-300 px-4 py-2">
                    {startIndex + index + 1}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {data?.employee_id}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="flex items-center gap-4">
                      <img
                        src={
                          data.profile_picture
                            ? data.profile_picture
                            : fakeProfile
                        }
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      {data?.employee_full_name}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {data?.roles?.[0]?.role_name || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <button
                      onClick={() => handleEditPermissions(data)}
                      className="text-primary-hrms text-2xl transition-colors"
                    >
                      <RiEditLine />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                >
                  {searchQuery
                    ? "No employees found matching your search"
                    : "No Data Available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="pagination flex justify-between items-center mt-6">
          <div>
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="p-1 text-xs text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fa-solid fa-arrow-left-long"></i> Previous
            </button>
          </div>

          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              const startPage = Math.max(1, currentPage - 5);
              const endPage = Math.min(totalPages, startPage + 9);

              return (
                pageNumber >= startPage &&
                pageNumber <= endPage && (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-2.5 py-1.5 rounded-md ${
                      currentPage === pageNumber
                        ? "bg-primary-hrms text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              );
            })}
          </div>

          <div>
            <button
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              className="p-1 text-xs text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <i className="fa-solid fa-arrow-right-long"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;
